import { useLayoutEffect, useRef, useState } from "react";

/**
 * Tính max-height để phần tử (vd khung bảng) kéo dài vừa tới đáy viewport: chiều cao cửa sổ trừ vị trí đỉnh
 * của phần tử trong trang và khoảng đệm phía dưới. Nội dung vượt quá thì phần tử tự cuộn (cần `overflow-auto`).
 * Tính lại khi resize cửa sổ hoặc khi phần phía trên đổi chiều cao (vd thanh tab/search xuống dòng).
 *
 * Khi BÊN DƯỚI khung còn nội dung phải luôn nhìn thấy (vd thanh phân trang): gắn thêm `containerRef` vào phần
 * tử bao cả khung lẫn phần đó. Hook tự đo khoảng từ đáy khung tới đáy phần tử bao và chừa lại — không phải
 * ước lượng chiều cao thanh phân trang bằng tay. `bottomGap` khi đó chỉ còn là phần đệm NGOÀI phần tử bao.
 */
export function useViewportMaxHeight<T extends HTMLElement, C extends HTMLElement = HTMLDivElement>(
  bottomGap = 16,
  minHeight = 240,
) {
  const ref = useRef<T>(null);
  const containerRef = useRef<C>(null);
  const [maxHeight, setMaxHeight] = useState<number>();

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const container = containerRef.current;

    const update = () => {
      const rect = el.getBoundingClientRect();
      // Cộng scrollY để kết quả không phụ thuộc trang đang cuộn tới đâu.
      const top = rect.top + window.scrollY;
      // Phần nằm dưới khung nhưng trong phần tử bao (phân trang...) — không đổi theo chiều cao của khung.
      const below = container ? Math.max(0, container.getBoundingClientRect().bottom - rect.bottom) : 0;
      setMaxHeight(Math.max(minHeight, window.innerHeight - top - below - bottomGap));
    };

    update();
    window.addEventListener("resize", update);
    const observer = new ResizeObserver(update);
    observer.observe(document.body);
    if (container) observer.observe(container);

    return () => {
      window.removeEventListener("resize", update);
      observer.disconnect();
    };
  }, [bottomGap, minHeight]);

  return { ref, containerRef, maxHeight };
}
